import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useMutation } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import { useState } from "react";
import { IRetornoEmitirBoleto } from "../../../../../../types/mutations/financeiro/emitir-boleto";
import { formatarValorMonetario } from "../../../../../../helpers/masks";
import { TParametrosPagamentoComValor } from ".";
import PaperDraggable from "../../../../../../components/PaperDraggable";
import PrintIcon from "@mui/icons-material/Print";
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';

export default function DialogEmitirBoleto({
    open,
    titulo,
    parametros,
    loja_id,
    onClose
}: DialogEmitirBoletoProps) {

    const defaultTitulos = parametros && parametros.parcelas.map((parcela, index) => {
        const sequenciaTitulo = (index + 1).toString().padStart(2, '0');
        return `${titulo}/${sequenciaTitulo}`
    })

    const [dadosBoleto, setDadosBoleto] = useState<IRetornoEmitirBoletoMultiple[] | null>(null)
    const [titulos, setTitulos] = useState<string[]>(defaultTitulos ? defaultTitulos : [])
    //queries e mutations
    const { mutate: emitirBoleto, isLoading } = useMutation({
        mutationKey: 'emitir-boleto',
        mutationFn: () => {
            return axiosClient.post(`/comercial/emitir-boletos-individuais`, {
                titulos: titulos,
                loja_id
            })
        },
        onError: () => setDadosBoleto(null),
        onSuccess: ({ data }: { data: IRetornoEmitirBoletoMultiple[] }) => {
            setDadosBoleto((prevState) => {
                if (!prevState) return data;
                
                const novosItens = data.filter(novoItem => 
                    !prevState.some(itemExistente => itemExistente.titulo === novoItem.titulo)
                );
                
                return [...prevState, ...novosItens];
            })
        }
    });

    const handleImprimir = (pdfFileURI: string) => {
        window.open(pdfFileURI, '_blank');
    };

    function verificarBoletoEmitido(titulo: string[]): boolean {
        if (!dadosBoleto) return false;
        return titulo.some(t => dadosBoleto.some(b => b.titulo === t));
    }

    return (
        <Dialog
            open={open}
            maxWidth='lg'
            fullWidth
            PaperComponent={PaperDraggable}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">Emitir boleto bancário</DialogTitle>
            <DialogContent>
                {
                    dadosBoleto &&
                    <Box mb={2} display="flex" flexDirection="column" gap={2}>
                        <Alert severity="warning">A emissão do boleto não garante a liquidação do título. O pagamento só será registrado após a compensação bancária</Alert>
                    </Box>
                }
                <TableContainer sx={{ border: "1px solid #ccc", borderRadius: 2 }}>
                    <Table size="small" aria-label="a dense table">
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell><b>Título</b></TableCell>
                                <TableCell><b>Parcela</b></TableCell>
                                <TableCell><b>Vencimento</b></TableCell>
                                {
                                    isLoading &&
                                    <>
                                        <TableCell width={500}>
                                            <Skeleton variant="text" width={'20%'} height={20} />
                                        </TableCell>
                                        <TableCell>
                                        </TableCell>
                                    </>
                                }
                                {dadosBoleto ?
                                    (<>
                                        <TableCell><b>Linha dígitavel</b></TableCell>
                                        <TableCell></TableCell>
                                    </>) :
                                    (<>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                    </>)
                                }
                            </TableRow>
                        </TableHead>
                        <TableBody>

                            {
                                parametros.parcelas.map((item: TParcela, index: number) => {
                                    const sequenciaTitulo = (index + 1).toString().padStart(2, '0');
                                    const selected = titulos && titulos.includes(`${titulo}/${sequenciaTitulo}`)
                                    const boleto = dadosBoleto ? dadosBoleto.find(b => b.titulo === `${titulo}/${sequenciaTitulo}`) : null;
                                    return (
                                        <TableRow
                                            key={index}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    color="primary"
                                                    checked={selected}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        setTitulos((prev) => {
                                                            if (selected) {
                                                                return prev.filter((t) => t !== `${titulo}/${sequenciaTitulo}`)
                                                            }
                                                            return [...prev, `${titulo}/${sequenciaTitulo}`]
                                                        })
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {`${titulo}/${sequenciaTitulo}`}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {`${index + 1}ª ${formatarValorMonetario(item.valor)}`}
                                            </TableCell>
                                            <TableCell>
                                                {item.vencimento}
                                            </TableCell>
                                            {
                                                isLoading &&
                                                <>
                                                    <TableCell>
                                                        <Skeleton variant="text" width="400px" height={20} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box display="flex" justifyContent="flex-end" gap={1}>
                                                            <Skeleton variant="circular" width={24} height={24} />
                                                            <Skeleton variant="circular" width={24} height={24} />
                                                        </Box>
                                                    </TableCell>
                                                </>
                                            }
                                            {dadosBoleto && boleto ? (
                                                <>
                                                    <TableCell >
                                                        <b>{boleto.linha_digitavel}</b>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="flex-end"
                                                        >
                                                            <IconButton onClick={() => {
                                                                navigator.clipboard.writeText(boleto.linha_digitavel)
                                                            }}>
                                                                <ContentCopyOutlinedIcon />
                                                            </IconButton>
                                                            <IconButton onClick={() => handleImprimir(boleto.boleto_url)}>
                                                                <PrintIcon />
                                                            </IconButton>
                                                        </Box>
                                                    </TableCell>
                                                </>
                                            ) : <>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                            </>}
                                        </TableRow>
                                    )
                                })
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
                <Button
                    onClick={() => emitirBoleto()}
                    variant="contained"
                    disabled={isLoading || titulos.length === 0 || verificarBoletoEmitido(titulos)}
                >
                    Emitir
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface DialogEmitirBoletoProps {
    open: boolean;
    titulo: string | number | null;
    parametros: TParametrosPagamentoComValor;
    loja_id: number
    onClose: () => void;
}

interface IRetornoEmitirBoletoMultiple extends IRetornoEmitirBoleto {
    titulo: string;
}

type TParcela = {
    valor: number,
    vencimento: string
}